<template>
  <Drawer
    :id="drawerId"
    title="Project Details"
    :loading="loading"
    padded
  >
    <div>
      <!-- Indicate if their team owner has rejected their work-->
      <v-row
        v-if="teamOwnerRejected"
        dense
      >
        <v-col cols="12">
          <v-alert
            text
            dense
            outlined
            color="error"
            class="body-1 mb-0"
          >
            Your team owner has rejected this project.
          </v-alert>
        </v-col>
      </v-row>

      <!-- Show any rejection note that may exist -->
      <v-row
        v-if="project.rejectionNote"
        dense
      >
        <v-col cols="12">
          <v-alert
            text
            dense
            outlined
            color="error"
            class="body-1 mb-0"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                Issues have been found.
              </div>
              <v-btn
                text
                @click="showRejectionNote"
              >
                View
              </v-btn>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <div class="text-h6 text-truncate mt-4">
        {{ project.name }}
      </div>

      <IconList
        :items="iconList"
        class="mt-3"
      />

      <!-- Row for editing, releasing & marking ready for review -->
      <v-row
        no-gutters
        align="center"
      >
        <v-col
          v-if="showEditorButton"
          cols="auto"
        >
          <EditorButton
            :editorAccess="project.userProject.editorAccess"
            openInNewTab
            class="mr-3"
          />
        </v-col>
        <v-col
          v-if="claimed"
          cols="auto"
        >
          <v-btn
            color="error"
            outlined
            elevation="0"
            class="mr-3"
            rounded
            :disabled="processing"
            @click="release"
          >
            Release
          </v-btn>
        </v-col>
        <v-col
          v-if="claimed || underReview"
          cols="auto"
        >
          <v-checkbox
            v-model="underReview"
            :disabled="processing"
            label="Ready for Review"
          >
            <template v-slot:label>
              <div class="body-2 dark--text d-flex">
                Ready for Review
              </div>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>

      <!-- Team owner approval row -->
      <v-row
        v-if="viewingAsTeamOwner && underReview"
        no-gutters
      >
        <v-col cols="12">
          <v-alert
            color="primary"
            outlined
            dense
          >
            <div class="d-flex align-center body-1 justify-space-between">
              Team Owner Approved?
              <div>
                <v-btn
                  icon
                  :color="teamOwnerRejected ? 'error' : ''"
                  @click="setTeamOwnerApproval(false)"
                >
                  <v-icon>{{ icons.thumbDown }}</v-icon>
                </v-btn>
                <v-btn
                  icon
                  :color="teamOwnerApproved ? 'primary' : ''"
                  @click="setTeamOwnerApproval(true)"
                >
                  <v-icon>{{ icons.thumbUp }}</v-icon>
                </v-btn>
              </div>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Claim button-->
      <v-row no-gutters>
        <v-col
          v-if="available"
          cols="12"
        >
          <v-btn
            color="primary"
            elevation="0"
            block
            class="my-5"
            large
            rounded
            :disabled="processing"
            @click="claim"
          >
            Claim
          </v-btn>
        </v-col>
      </v-row>

      <!-- Preview images-->
      <v-row
        v-if="images.length"
        dense
      >
        <v-col
          v-for="(image, index) in images"
          :key="index"
          cols="6"
        >
          <div class="pa-1 rounded-lg bordered border-light d-flex align-center justify-center">
            <v-img
              :src="image.thumbUrl"
              max-height="300"
              contain
            />
          </div>
        </v-col>
      </v-row>

      <!-- Details & notes-->
      <v-sheet
        class="rounded-lg bordered border-light pa-3 mt-5 body-2"
      >
        <div class="body-1 font-weight-bold">
          About this project
        </div>
        <div class="mt-2 mb-6">
          Use this to help guide you while you design
        </div>
        <template v-if="project.inspirationImageUrls">
          <v-text-field
            v-for="(url, index) in project.inspirationImageUrls"
            :key="index"
            outlined
            hide-details
            dense
            label="Inspirational image"
            :value="url"
            class="body-2 mb-3"
          >
            <template v-slot:append>
              <v-icon @click="openImage(url)">
                {{ openInNewIcon }}
              </v-icon>
            </template>
          </v-text-field>
        </template>

        <!-- Notes button + dialog that shows notes -->
        <v-dialog
          v-if="notes.length"
          v-model="showNotes"
          content-class="align-self-start rounded-lg overflow-hidden notes-dialog"
          width="800px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              block
              elevation="0"
              class="mt-3 bordered border-light"
              v-bind="attrs"
              v-on="on"
            >
              View Notes ({{ notes.length }})
            </v-btn>
          </template>
          <v-card class="h-full d-flex-hide flex-column">
            <v-tabs
              v-model="activeNote"
              background-color="gray"
              dark
            >
              <v-tab
                v-for="note in notes"
                :key="note.id"
              >
                {{ note.name }}
              </v-tab>
            </v-tabs>
            <v-tabs-items
              v-model="activeNote"
              class="h-full"
            >
              <v-tab-item
                v-for="note in notes"
                :key="note.id"
                class="h-full d-flex-hide"
              >
                <v-card
                  flat
                  class="py-5 px-7 autoscroll-y"
                >
                  <div v-html="note.notes" />
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-dialog>
      </v-sheet>
    </div>
  </Drawer>
</template>

<script>
import { mdiCircleMedium, mdiClockOutline, mdiRuler, mdiCurrencyUsd, mdiOpenInNew, mdiThumbUp, mdiThumbUpOutline, mdiThumbDown, mdiThumbDownOutline } from '@mdi/js';
import Drawer from '../../layout/Drawer';
import IconList from '../../utilities/IconList';
import EditorButton from '../../utilities/EditorButton';
import { DESIGN_PROJECT_STATUS, DRAWERS } from '../../../utils/constants';
import getNestedValue from '../../../utils/getNestedValue';
import { cloneDeep } from 'lodash';

export default {
  name: 'DesignProjectDrawer',

  components: {
    Drawer,
    IconList,
    EditorButton
  },

  data () {
    return {
      dotIcon: mdiCircleMedium,
      openInNewIcon: mdiOpenInNew,
      project: {},
      loading: false,
      processing: false,
      drawerId: DRAWERS.CONTRIBUTOR_DESIGN_PROJECT_DRAWER,
      icons: {
        thumbUp: mdiThumbUp,
        thumbUpOutline: mdiThumbUpOutline,
        thumbDown: mdiThumbDown,
        thumbDownOutline: mdiThumbDownOutline
      },
      showNotes: false,
      activeNote: null
    };
  },

  computed: {
    selectedProjectId () {
      return this.$store.state.contributorDesignProjects.selectedProjectId;
    },

    claimed () {
      return this.project.statusId === DESIGN_PROJECT_STATUS.ASSIGNED;
    },

    available () {
      return this.project.statusId === DESIGN_PROJECT_STATUS.OPEN;
    },

    viewingAsTeamOwner () {
      return this.$store.state.contributorDesignProjects.showTeam;
    },

    teamOwnerApproved () {
      return this.project.teamOwnerApproved === true;
    },

    teamOwnerRejected () {
      return this.project.teamOwnerApproved === false;
    },

    showEditorButton () {
      if (!this.project.userProject) return false;
      return this.claimed || this.viewingAsTeamOwner;
    },

    underReview: {
      get () {
        return this.project.statusId === DESIGN_PROJECT_STATUS.UNDER_REVIEW;
      },
      set () {
        var action = this.project.statusId === DESIGN_PROJECT_STATUS.UNDER_REVIEW ? 'retractReview' : 'submitReview';
        this.processing = true;
        this.$store.dispatch('contributorDesignProjects/' + action, this.project.id)
          .then((project) => {
            this.project = Object.assign(project, cloneDeep(this.project));
            this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
          })
          .finally(() => {
            this.processing = false;
          });
      }
    },

    iconList () {
      var list = [{
        text: this.project.lastSavedDateDisplay,
        icon: mdiClockOutline
      }, {
        text: this.project.product?.toLowerCase(),
        icon: mdiRuler
      }, {
        id: 'price',
        text: this.project.priceDisplay,
        icon: mdiCurrencyUsd
      }];
      if (this.$store.getters['contributorDesignProjects/hidePrice']) {
        list = list.filter(x => x.id !== 'price');
      }
      return list;
    },

    images () {
      return (getNestedValue(this.project, 'userProject', 'images') || []).filter(x => !x.isPresentation);
    },

    notes () {
      var obj = [];
      if (this.project.notes) {
        obj.push({ id: 'project', name: 'Project', notes: this.project.notes });
      }
      if (this.project.categoryNotes) {
        obj.push({ id: 'category', name: 'Category', notes: this.project.categoryNotes });
      }
      if (this.project.industryNotes) {
        obj.push({ id: 'industry', name: 'Industry', notes: this.project.industryNotes });
      }
      if (this.project.topicNotes) {
        obj.push({ id: 'topic', name: 'Topic', notes: this.project.topicNotes });
      }
      if (this.project.designGroupNotes) {
        obj.push({ id: 'designGroup', name: 'Design Group', notes: this.project.designGroupNotes });
      }
      if (this.project.designGroupCategoryNotes) {
        obj.push({ id: 'designGroupCategory', name: 'Desgn Group/Category', notes: this.project.designGroupCategoryNotes });
      }
      return obj;
    },

    drawerOpenedAt () {
      return this.$store.state.drawers.lastOpenedAt;
    }
  },

  watch: {
    drawerOpenedAt (val) {
      if (this.drawerId === this.$store.getters['drawers/topDrawer']?.id) {
        this.loading = true;
        this.$store.dispatch('contributorDesignProjects/getDetail', this.$store.state.contributorDesignProjects.selectedProjectId)
          .then(project => {
            this.project = project;
          })
          .finally(() => {
            setTimeout(() => {
              this.loading = false;
            }, 500);
          });
      }
    }
  },

  methods: {
    release () {
      this.processing = true;
      this.$store.dispatch('contributorDesignProjects/release', this.project.id)
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    claim () {
      this.processing = true;
      this.$store.dispatch('contributorDesignProjects/claim', this.project.id)
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        }).catch(error => {
          this.$root.$alert('Unable to claim project', error.response.data.exceptionMessage, { hideSupport: true, cancelText: '' });
          this.$emit('load');
          console.log(error);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    setTeamOwnerApproval (val) {
      this.processing = true;
      this.$store.dispatch('contributorDesignProjects/setTeamOwnerApproved', { projectId: this.project.id, approved: val })
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
        })
        .finally(() => {
          this.processing = false;
        });
    },

    openImage (url) {
      window.open(url, '_blank');
    },

    openNotes () {
      this.$root.$alert('Project Notes', this.project.notes, { width: 800, cancelText: '', okText: 'Close' });
    },

    openDesignGroupCategoryNotes () {
      this.$root.$alert('Design Group Category Notes', this.project.designGroupCategoryNotes, { width: 800, cancelText: '', okText: 'Close' });
    },

    showRejectionNote () {
      this.$root.$alert('Errors Found', this.project.rejectionNote);
    }
  }
};
</script>

  <style scoped>
  >>>.notes-dialog {
    height:90%;
  }
  </style>
